import React, { useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lightbulb, MessageSquare, ShieldAlert, Send, CheckCircle2 } from "lucide-react";

const CATEGORIES = ["Suggestion", "Idea", "Opinion", "Other"];

export default function FeedbackSection() {
  return (
    <section className="py-24 bg-brand-navy text-white relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-[0.06]" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="bg-brand-orange text-white border-none mb-4">
              <MessageSquare className="w-3 h-3 mr-1" /> Your voice matters
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white">
              Ideas, suggestions and opinions on <span className="text-brand-orange">e-Governance software</span>.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
              KELTA collects suggestions and opinions from technical assistants, citizens and
              officials on government IT platforms. Genuine inputs are compiled and forwarded to
              the concerned department for review.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              {[
                "Anonymous submission allowed — email & phone optional",
                "Reviewed only by the KELTA Core Team",
                "Consolidated inputs shared with concerned authorities",
              ].map(t => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="flex justify-center lg:justify-end"
          >
            <Card className="bg-white/5 backdrop-blur border border-white/10 p-6 w-full max-w-md text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 rounded-full bg-brand-orange/20 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-brand-orange" />
                </div>
                <div>
                  <div className="font-bold text-lg">Share your input</div>
                  <div className="text-xs text-slate-400">Reaches the KELTA Core Team directly</div>
                </div>
              </div>
              <p className="text-sm text-slate-300 mt-3">
                Report an issue in a government portal, suggest a feature, or share a policy idea.
                All fields except <em>name</em>, <em>category</em>, <em>subject</em> and <em>message</em> are optional.
              </p>
              <FeedbackDialog>
                <Button className="mt-5 w-full bg-brand-orange hover:bg-brand-orange/90 text-white h-11" data-testid="open-feedback-btn">
                  <Send className="w-4 h-4 mr-2" /> Submit Feedback
                </Button>
              </FeedbackDialog>
              <div className="flex items-center gap-2 text-xs text-slate-400 mt-3">
                <ShieldAlert className="w-3.5 h-3.5" />
                Submissions are confidential and visible only to KELTA super admin.
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function FeedbackDialog({ children }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const empty = {
    name: "", email: "", phone: "",
    category: "Suggestion", subject: "", message: "",
    department: "",
  };
  const [form, setForm] = useState(empty);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill name, subject and message"); return;
    }
    try {
      setBusy(true);
      const payload = { ...form };
      if (!payload.email) delete payload.email; // omit empty email so EmailStr validation is skipped
      await api.post("/feedback", payload);
      toast.success("Thanks! Your feedback has been received.");
      setOpen(false);
      setForm(empty);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-brand-orange" /> Suggestion / Complaint / Idea
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Your Name *</Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} required data-testid="fb-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email (optional)</Label>
              <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="fb-email" />
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="fb-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger data-testid="fb-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department / Concerned Body (optional)</Label>
              <Input value={form.department} onChange={e => set("department", e.target.value)} data-testid="fb-dept" />
            </div>
          </div>
          <div>
            <Label>Subject *</Label>
            <Input value={form.subject} onChange={e => set("subject", e.target.value)} required data-testid="fb-subject" />
          </div>
          <div>
            <Label>Message *</Label>
            <Textarea rows={5} value={form.message} onChange={e => set("message", e.target.value)} required data-testid="fb-message" placeholder="Describe your idea, issue or suggestion in detail…" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand-orange text-white hover:bg-brand-orange/90" data-testid="fb-submit-btn">
              {busy ? "Sending…" : "Submit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
